/**
 * Field Test Models
 *
 * Interfaces for field test records and custom field test submissions.
 */

// ============================================
// FIELD TEST TYPES
// ============================================

export type FieldTestType = 'pre-flight' | 'post-flight' | 'equipment' | 'calibration';

export type FieldTestStatus = 'passed' | 'failed' | 'pending';

export type CustomFieldTestType =
  | 'nozzle-calibration'
  | 'equipment-inspection'
  | 'spray-pattern'
  | 'flow-rate'
  | 'tank-calibration'
  | 'other';

// ============================================
// FIELD TEST INTERFACES
// ============================================

export interface FieldTestChecklistItem {
  item: string;
  passed: boolean;
  notes?: string;
}

export interface FieldTest {
  id: string;
  title: string;
  testType: FieldTestType;
  aircraftId?: string;
  aircraft: string;  // aircraftName for display
  pilotId?: string;
  pilot: string;  // pilotName for display
  date: string;
  location: string;
  status: FieldTestStatus;
  checklist: FieldTestChecklistItem[];
  notes?: string;
}

// ============================================
// CUSTOM FIELD TEST SUBMISSION INTERFACES
// ============================================

/**
 * File attached to a custom field test submission.
 * Note: Files are private - use FieldTestService.getDownloadUrl() to get
 * a temporary signed URL for viewing/downloading.
 */
export interface FieldTestFile {
  id?: string;  // File ID from database (present for existing files)
  name: string;
  type: string;
  filePath: string;  // Path only - use getDownloadUrl() for access
  size?: number;
}

export interface CustomFieldTest {
  id: string;
  title: string;
  testType: CustomFieldTestType;
  category: string;
  datePerformed: string;
  description?: string;
  files: FieldTestFile[];
  applicableAircraft: string;
  frequency: string;
  requiresSignoff: boolean;
  createdDate: string;
  usageCount: number;
}

// ============================================
// FIELD TEST TYPE OPTIONS
// ============================================

export const FIELD_TEST_TYPE_OPTIONS = [
  { value: 'pre-flight', label: 'Pre-Flight Check' },
  { value: 'post-flight', label: 'Post-Flight Inspection' },
  { value: 'equipment', label: 'Equipment Test' },
  { value: 'calibration', label: 'Calibration Test' }
] as const;

export const CUSTOM_FIELD_TEST_TYPE_OPTIONS = [
  { value: 'nozzle-calibration', label: 'Nozzle Calibration' },
  { value: 'equipment-inspection', label: 'Equipment Inspection' },
  { value: 'spray-pattern', label: 'Spray Pattern Test' },
  { value: 'flow-rate', label: 'Flow Rate Test' },
  { value: 'tank-calibration', label: 'Tank Calibration' },
  { value: 'other', label: 'Other' }
] as const;
